import { ref, onMounted } from 'vue'
import { useRouter, useLocalePath } from '#imports'
import { useInternationalization } from '@shopware/composables'
import type { ParsedKevelAd } from '../utils/parseKevelDecision'
import { validateKevelTargetUrl } from '../utils/validateKevelTargetUrl'

export interface KevelAdTrackingOptions {
    /** Optional product ID for tracking context */
    productId?: string
    /** Component name for tracking context (e.g., "content-banner-text") */
    component?: string
    /** Position index for tracking context */
    position?: number
}

/**
 * Shared tracking logic for all Kevel ad zone components.
 * Handles impression pixel firing and click tracking with navigation.
 *
 * Per issue #123:
 * - Impressions fire once the ad is visible (on mount)
 * - Clicks navigate through Vue router (no page reload)
 * - ctTargetUrl (a Shopware category id) is validated and resolved to /navigation/<id>
 * - Invalid targets are treated as no-fill
 * - Tracking includes productId, component, position (within 1024-char limit)
 */
export function useKevelAdTracking(
    ad: ParsedKevelAd,
    options: KevelAdTrackingOptions = {}
) {
    const impressionTracked = ref(false)
    const router = useRouter()
    const localePath = useLocalePath()
    const { formatLink } = useInternationalization(localePath)

    // Validate and resolve target URL
    const validatedUrl = validateKevelTargetUrl(ad.targetUrl)
    const resolvedPath = validatedUrl ? formatLink(validatedUrl.path) : null

    // Build tracking parameters (stay within 1024-char limit)
    function buildTrackingParams(): string {
        const params = new URLSearchParams()

        if (options.productId) params.set('productId', options.productId)
        if (options.component) params.set('component', options.component)
        if (options.position !== undefined) params.set('position', String(options.position))

        const paramString = params.toString()
        return paramString ? `&${paramString}` : ''
    }

    // Fire impression tracking pixel on mount via first-party proxy
    // Only fire if ad has valid target URL OR no target URL at all (display-only ad)
    const shouldTrack = !ad.targetUrl || validatedUrl?.isValid

    onMounted(async () => {
        if (!impressionTracked.value && ad.impressionUrl && shouldTrack) {
            const trackingParams = buildTrackingParams()
            const impressionUrl = `${ad.impressionUrl}${trackingParams}`

            // Check 1024-character limit
            if (impressionUrl.length > 1024) {
                console.warn('[kevel] Impression URL exceeds 1024 characters, truncating params')
                // Fallback: send without custom params
                await sendTracking(ad.impressionUrl)
                impressionTracked.value = true
                return
            }

            await sendTracking(impressionUrl)
            impressionTracked.value = true
        }
    })

    // Send tracking request through first-party proxy
    async function sendTracking(url: string) {
        try {
            await $fetch('/api/kevel/track', {
                method: 'POST',
                body: { url },
            })
        } catch (error) {
            console.error('[kevel] Tracking request failed:', error)
        }
    }

    // Track click and navigate
    async function trackClick(event?: MouseEvent) {
        // Prevent default link behavior if event is provided
        if (event) {
            event.preventDefault()
        }

        if (!validatedUrl?.isValid || !resolvedPath) {
            console.warn('[kevel] Cannot navigate, invalid or unresolvable target URL')
            return
        }

        // Fire click tracking via first-party proxy
        if (ad.clickUrl) {
            const trackingParams = buildTrackingParams()
            const clickUrl = `${ad.clickUrl}&noredirect${trackingParams}`

            // Check 1024-character limit
            if (clickUrl.length <= 1024) {
                // Fire tracking in background (don't await, navigation shouldn't be blocked)
                sendTracking(clickUrl).catch(() => {
                    // Ignore errors, tracking is non-critical
                })
            } else {
                console.warn('[kevel] Click URL exceeds 1024 characters, sending without params')
                sendTracking(`${ad.clickUrl}&noredirect`).catch(() => {})
            }
        }

        // Navigate via Vue router (no page reload)
        await router.push(resolvedPath)
    }

    return {
        trackClick,
        resolvedPath,
        isValidTarget: shouldTrack,
    }
}
